import { forwardRef, useMemo } from 'react';
import {
  BackSide,
  ColorRepresentation,
  Group,
  Layers,
  Matrix4,
  Shape,
  ShapeGeometry,
  Triangle,
  Vector2,
  Vector2Tuple,
  Vector3,
  Vector3Tuple,
} from 'three';

import { Line, useCursor } from '@react-three/drei';
import { GroupProps } from '@react-three/fiber';
import { toVector2 } from '@spazious/dollhouse-three';

export interface PolygonProps extends GroupProps {
  points: (Vector2Tuple | Vector2 | Vector3Tuple | Vector3)[];
  color?: ColorRepresentation;
  dashed?: boolean;
  lineWidth?: number;
  backgroundOpacity?: number;
  fill?: boolean;
  cursor?: string;
  hovered?: boolean;
  layers?: Layers;
}

export const Polygon = forwardRef<Group, PolygonProps>(
  (
    {
      points,
      color = 'black',
      dashed,
      lineWidth = 1,
      backgroundOpacity = 0.1,
      fill = true,
      cursor = 'auto',
      hovered = false,
      layers,
      userData,
      ...restProps
    },
    ref
  ) => {
    const up = useMemo(() => new Vector3(0, 1, 0), []);

    const mt = useMemo(() => new Matrix4().lookAt(new Vector3(0, 0, 0), up, up), [up]);
    const [is3D, isArray] = useMemo(() => {
      if (points.length === 0) return [false, false];
      if (Array.isArray(points[0])) {
        return [points[0].length === 3, true];
      }
      return [points[0] instanceof Vector3, false];
    }, [points]);

    const v3Points = useMemo(() => {
      return is3D
        ? isArray
          ? points.map((point) => new Vector3().fromArray(point as Vector3Tuple))
          : points.map((point) => (point as Vector3).clone())
        : points.map((point) =>
            new Vector3().fromArray([...toVector2(point).toArray(), 0]).applyMatrix4(mt)
          );
    }, [is3D, isArray, mt, points]);

    const polyShapeGeometry = useMemo(() => {
      if (is3D) {
        const [p0, p1, p2] = isArray
          ? points.slice(0, 3).map((p) => new Vector3().fromArray(p as Vector3Tuple))
          : [points[0] as Vector3, points[1] as Vector3, points[2] as Vector3];

        const normal = new Triangle(p0, p1, p2).getNormal(new Vector3());

        const u = new Vector3().subVectors(p1, p0).normalize();
        const v = new Vector3().crossVectors(normal, u).normalize();

        const points2D = points.map((p) => {
          const vec = new Vector3().subVectors(p as Vector3, p0); // relative to origin
          return new Vector2(vec.dot(u), vec.dot(v));
        });

        const basis = new Matrix4().makeBasis(u, v, normal).setPosition(p0);
        return new ShapeGeometry(new Shape().setFromPoints(points2D)).applyMatrix4(basis);
      } else {
        return new ShapeGeometry(
          new Shape().setFromPoints(points.map((v) => toVector2(v)))
        ).applyMatrix4(mt);
      }
    }, [mt, points, isArray, is3D]);

    useCursor(hovered, cursor);

    return (
      <group ref={ref} {...restProps}>
        {fill && (
          <mesh geometry={polyShapeGeometry} layers={layers} name="polygon" userData={userData}>
            <meshBasicMaterial
              polygonOffset
              transparent
              color={color}
              opacity={backgroundOpacity}
              polygonOffsetUnits={-10}
              side={BackSide}
            />
          </mesh>
        )}
        <Line
          polygonOffset
          color={color}
          dashed={dashed}
          depthTest={false}
          lineWidth={lineWidth}
          points={v3Points}
          polygonOffsetUnits={-20}
          renderOrder={1004}
        />
      </group>
    );
  }
);
